import { useState } from 'react';
import { Link } from 'react-router-dom';
import SchemaOrg from '../components/SchemaOrg';
import { useAuth } from '../hooks/useAuth';
import {
  ARTICLES,
  CATEGORIES,
  type Category,
} from '@/data/articles';
import { ThemeTransitionOverlay } from '../components/animations/ThemeTransitionOverlay';
import { ArticleCardEntrance } from '../components/animations/ArticleCardEntrance';
import { CountdownRing } from '../components/animations/CountdownRing';
import { useThemeCarousel } from '@/contexts/ThemeCarouselContext';
import { THEME_REGISTRY } from '@/themes/index';

const LANDING_SCHEMA = {
  title: 'Reporter — Southern Africa stories',
  subtitle: 'People, heritage, wildlife, and culture from across the region.',
  author: 'Reporter',
  publishedAt: '2026-05-22T00:00:00Z',
  breadcrumbs: [{ label: 'Home', href: '/' }],
};

export default function LandingPage() {
  const { user } = useAuth();
  const { currentThemeId } = useThemeCarousel();
  const accentColor = THEME_REGISTRY[currentThemeId]?.accentColor ?? '#D4A843';
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [transitionTrigger, setTransitionTrigger] = useState(0);

  const featured = ARTICLES.find((a) => a.featured) ?? ARTICLES[0];
  const sidebarArticles = ARTICLES.filter((a) => !a.featured).slice(0, 2);
  const sidebarIds = new Set(sidebarArticles.map((a) => a.id));
  const gridArticles = ARTICLES.filter(
    (a) => !a.featured && !sidebarIds.has(a.id)
  ).filter((a) => activeCategory === null || a.category === activeCategory);

  return (
    <div className="bg-surface text-on-surface min-h-screen flex flex-col font-body-md antialiased selection:bg-primary-container selection:text-ink-black pt-9">
      <SchemaOrg schemaTypes={['BreadcrumbList']} hero={LANDING_SCHEMA} canonicalUrl="/" />
      <ThemeTransitionOverlay trigger={transitionTrigger} />

      {/* ── Safari Marketing Hero (unauthenticated only) ── */}
      {!user && (
        <section className="relative w-full bg-espresso overflow-hidden">
          {/* Safari nav */}
          <nav className="relative z-20 flex items-center justify-between px-6 py-4 md:px-12 border-t-2 border-t-terracotta border-b border-ash">
            <div className="flex gap-6 font-label-caps text-label-caps tracking-widest uppercase">
              <Link to="/" className="text-cream hover:text-terracotta transition-colors duration-200">Destinations</Link>
              <Link to="/" className="text-cream hover:text-terracotta transition-colors duration-200 hidden md:block">Stories</Link>
              <Link to="/" className="text-cream hover:text-terracotta transition-colors duration-200 hidden md:block">About</Link>
            </div>
            <div className="absolute left-1/2 -translate-x-1/2">
              <span className="font-display-hero text-[22px] md:text-[26px] tracking-tighter text-cream uppercase">
                REPORTER
              </span>
            </div>
            <Link
              to="/register"
              className="font-label-caps text-label-caps uppercase tracking-widest px-5 py-2 bg-terracotta text-cream hover:bg-dusk transition-colors duration-200"
              style={{ borderRadius: 0 }}
            >
              Start free
            </Link>
          </nav>

          {/* Full-bleed hero */}
          <div className="relative w-full h-[85vh] min-h-[520px]">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1600&auto=format&fit=crop&q=80)` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-espresso/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end px-6 md:px-16 pb-16 md:pb-24 max-w-7xl mx-auto">
              <div className="max-w-3xl">
                <p className="font-label-caps text-[11px] text-terracotta uppercase tracking-widest mb-4">
                  Africa's editorial publishing platform
                </p>
                <h1 className="font-display-hero text-display-hero-mobile md:text-[68px] text-cream leading-[1.05] tracking-tight mb-6">
                  Your stories deserve<br />a magazine.
                </h1>
                <p className="font-body-md text-[18px] text-sand mb-4 max-w-xl">
                  50 world-class editorial themes. NatGeo layouts, cinematic long-reads, Afrofuturist design. Write once — publish in any format.
                </p>
                <p className="font-meta-code text-[12px] text-fog uppercase tracking-widest mb-10">
                  Free to write &amp; publish · No credit card
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/register"
                    className="font-label-caps text-label-caps uppercase tracking-widest px-8 py-4 bg-terracotta text-cream hover:bg-dusk transition-colors duration-300 text-center"
                    style={{ borderRadius: 0 }}
                  >
                    Publish your first story free
                  </Link>
                  <Link
                    to="/login"
                    className="font-label-caps text-label-caps uppercase tracking-widest px-8 py-4 border border-cream/50 text-cream/70 hover:border-cream hover:text-cream transition-colors duration-300 text-center"
                    style={{ borderRadius: 0 }}
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Destination strip */}
          <div className="w-full bg-espresso border-b border-ash py-8 px-6 md:px-12">
            <div className="flex items-center justify-between mb-5 max-w-7xl mx-auto">
              <span className="font-label-caps text-label-caps text-fog uppercase tracking-widest">Stories from the field</span>
            </div>
            <div className="flex gap-4 overflow-x-auto no-scrollbar max-w-7xl mx-auto pb-2">
              {[
                { name: 'Kruger', img: 'photo-1547036967-23d11aacaee0' },
                { name: 'Cape Point', img: 'photo-1580060839134-75a5edca2e99' },
                { name: 'Mozambique', img: 'photo-1504432842672-1a79f78e4084' },
                { name: 'Victoria Falls', img: 'photo-1516026672322-bc52d61a55d5' },
                { name: 'Zanzibar', img: 'photo-1541410702738-f87a5449e456' },
                { name: 'Serengeti', img: 'photo-1474511320723-9a56873867b5' },
              ].map((dest) => (
                <div
                  key={dest.name}
                  className="group flex-shrink-0 relative w-36 md:w-44 aspect-[3/4] border border-ash hover:border-terracotta transition-colors duration-300 overflow-hidden cursor-pointer"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
                    style={{ backgroundImage: `url(https://images.unsplash.com/${dest.img}?w=400&auto=format&fit=crop&q=80)` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <span className="font-label-caps text-[11px] text-cream uppercase tracking-widest">{dest.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Value props */}
          <div className="w-full bg-espresso px-6 md:px-16 py-10 border-b border-ash">
            <div className="flex gap-12 md:gap-20 max-w-7xl mx-auto">
              {[
                { icon: 'palette', label: '50 Editorial Themes' },
                { icon: 'photo_camera', label: 'Full-Bleed Photography' },
                { icon: 'language', label: 'One Click to Publish' },
              ].map(({ icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-3">
                  <span className="material-symbols-outlined text-terracotta text-2xl">{icon}</span>
                  <span className="font-label-caps text-[10px] text-fog uppercase tracking-widest whitespace-nowrap">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top Navigation */}
      <nav
        className="bg-ink-black flex flex-col items-center w-full px-8 py-4 z-40 sticky top-9 border-b"
        style={{ borderBottomColor: accentColor }}
      >
        <div className="max-w-[1440px] w-full flex flex-col items-center gap-4">
          <div className="w-full flex justify-between items-center">
            <span className="material-symbols-outlined text-primary hover:opacity-80 transition-opacity cursor-pointer select-none">
              menu
            </span>
            <h1
              className="font-display-hero text-display-hero-mobile md:text-display-hero uppercase tracking-tighter transition-colors duration-1000"
              style={{ color: accentColor }}
            >
              REPORTER
            </h1>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setTransitionTrigger((n) => n + 1)}
                title="Preview theme transition"
                aria-label="Preview theme transition"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <CountdownRing size={32} strokeWidth={2.5} />
              </button>
              {user ? (
                <div className="flex items-center gap-4">
                  <Link
                    to="/stories"
                    className="font-label-caps text-label-caps text-meta-gray hover:text-primary transition-colors uppercase"
                  >
                    MY STORIES
                  </Link>
                  <Link
                    to="/editor"
                    className="font-label-caps text-label-caps text-primary hover:opacity-80 transition-opacity uppercase"
                  >
                    EDITOR
                  </Link>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="font-label-caps text-label-caps text-meta-gray hover:text-primary transition-colors uppercase"
                >
                  SIGN IN
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ── Destinations Strip ── */}
      <div className="w-full bg-ink-black border-b border-border-subtle/30 px-4 md:px-8 py-4">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex gap-3 overflow-x-auto no-scrollbar">
            {/* All button */}
            <button
              onClick={() => setActiveCategory(null)}
              className="group relative flex-shrink-0 w-28 h-36 overflow-hidden rounded-lg bg-black border border-white/10 flex flex-col justify-end"
              style={{
                outline: activeCategory === null ? `2px solid ${accentColor}` : 'none',
                outlineOffset: '2px',
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="material-symbols-outlined text-white/30 text-4xl">grid_view</span>
              </div>
              <div className="relative p-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">All</p>
              </div>
              {activeCategory === null && (
                <div className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />
              )}
            </button>

            {CATEGORIES.map((cat) => {
              const firstInCat = ARTICLES.find((a) => a.category === cat);
              const coverImage = firstInCat?.content.hero?.coverImage ?? '';
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(isActive ? null : cat)}
                  className="group relative flex-shrink-0 w-28 h-36 overflow-hidden rounded-lg bg-black flex flex-col justify-end"
                  style={{
                    outline: isActive ? `2px solid ${accentColor}` : 'none',
                    outlineOffset: '2px',
                  }}
                >
                  {coverImage && (
                    <div
                      className="absolute inset-0 group-hover:scale-[1.05] transition-transform duration-500"
                      style={{
                        backgroundImage: `url(${coverImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  )}
                  <div
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.25) 100%)',
                      opacity: isActive ? 0.95 : 0.7,
                    }}
                  />
                  <div className="relative p-2">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-white leading-tight">
                      {cat}
                    </p>
                  </div>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow w-full max-w-[1440px] mx-auto px-4 md:px-8 py-10 grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Featured Hero — 8 cols, full-bleed photo */}
        <article className="md:col-span-8">
          <Link
            to={`/story/${featured.id}`}
            className="group relative w-full aspect-video overflow-hidden block rounded-xl bg-black"
          >
            {featured.content.hero?.coverImage ? (
              <div
                className="absolute inset-0 group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                style={{
                  backgroundImage: `url(${featured.content.hero.coverImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${featured.gradientFrom} 0%, ${featured.gradientTo} 100%)`,
                }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 md:p-8 flex flex-col gap-3 w-full">
              <span
                className="font-label-caps text-[11px] font-bold uppercase tracking-widest border px-2 py-1 w-max"
                style={{ color: accentColor, borderColor: accentColor }}
              >
                {featured.category}
              </span>
              <h2 className="font-headline-lg text-2xl md:text-4xl text-white font-bold leading-tight drop-shadow-lg max-w-lg">
                {featured.title}
              </h2>
              <p className="text-sm text-white/70 line-clamp-2 max-w-md hidden md:block">
                {featured.excerpt}
              </p>
              <p className="font-meta-code text-[11px] text-white/50 uppercase tracking-widest">
                BY {featured.author} · {featured.readTime} MIN READ
              </p>
            </div>
          </Link>
        </article>

        {/* Sidebar — 4 cols */}
        <aside className="md:col-span-4 flex flex-col gap-4 md:pl-4">
          <h3 className="font-label-caps text-label-caps text-primary border-b border-border-subtle pb-2">
            ALSO THIS WEEK
          </h3>
          <div className="flex flex-col gap-4">
            {sidebarArticles.map((article) => {
              const coverImage = article.content.hero?.coverImage ?? '';
              return (
                <Link
                  key={article.id}
                  to={`/story/${article.id}`}
                  className="group relative w-full aspect-video overflow-hidden rounded-lg bg-black flex flex-col justify-end"
                >
                  {coverImage ? (
                    <div
                      className="absolute inset-0 group-hover:scale-[1.04] transition-transform duration-500"
                      style={{
                        backgroundImage: `url(${coverImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  ) : (
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(135deg, ${article.gradientFrom} 0%, ${article.gradientTo} 100%)`,
                      }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="relative p-3">
                    <span className="font-label-caps text-[9px] text-white/50 uppercase tracking-widest mb-1 block">
                      {article.category}
                    </span>
                    <h4 className="font-headline-md text-sm font-bold text-white leading-tight group-hover:text-primary transition-colors">
                      {article.title}
                    </h4>
                  </div>
                </Link>
              );
            })}
          </div>
        </aside>

        {/* Latest Dispatches Grid */}
        <section className="md:col-span-12 mt-10 pt-8 border-t border-border-subtle">
          <div className="flex justify-between items-end mb-6">
            <div className="flex flex-col gap-3">
              <span className="font-label-caps text-label-caps text-fog uppercase tracking-widest">
                Latest Dispatches
              </span>
              <div className="w-12 h-0.5 bg-terracotta" />
            </div>
            {activeCategory && (
              <button
                onClick={() => setActiveCategory(null)}
                className="font-meta-code text-meta-code text-meta-gray hover:text-on-surface transition-colors uppercase text-[11px]"
              >
                Clear filter ×
              </button>
            )}
          </div>

          {gridArticles.length === 0 ? (
            <p className="font-meta-code text-meta-code text-meta-gray text-center py-16 uppercase tracking-widest">
              No stories in this category yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {gridArticles.map((article, idx) => {
                const coverImage = article.content.hero?.coverImage ?? '';
                return (
                  <ArticleCardEntrance key={article.id} delay={idx * 80}>
                    <Link
                      to={`/story/${article.id}`}
                      className="group flex flex-col bg-bark border-l-2 border-l-transparent hover:border-l-terracotta transition-colors duration-300"
                    >
                      {/* Image — full landscape 16:9, never covered by text */}
                      <div className="relative w-full aspect-video overflow-hidden bg-black">
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={article.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500 ease-out"
                          />
                        ) : (
                          <div
                            className="absolute inset-0"
                            style={{
                              background: `linear-gradient(135deg, ${article.gradientFrom} 0%, ${article.gradientTo} 100%)`,
                            }}
                          />
                        )}
                        <span className="absolute top-3 left-3 font-label-caps text-[10px] uppercase tracking-widest px-2 py-1 bg-espresso/85 text-gold-leaf">
                          {article.category}
                        </span>
                      </div>

                      {/* Text block — sits below the image, not over it */}
                      <div className="flex flex-col gap-3 p-5">
                        <h4 className="font-headline-md text-[18px] text-cream leading-tight group-hover:text-terracotta transition-colors">
                          {article.title}
                        </h4>
                        <p className="font-body-md text-[13px] text-sand leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 pt-3 border-t border-ash">
                          <p className="font-label-caps text-[10px] text-fog uppercase tracking-widest">
                            {article.author} · {article.readTime} min read
                          </p>
                        </div>
                      </div>
                    </Link>
                  </ArticleCardEntrance>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-espresso border-t border-ash w-full px-8 py-16 flex flex-col items-center gap-10 mt-auto">
        <div className="w-12 h-0.5 bg-terracotta" />
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <h2 className="font-display-hero text-[32px] md:text-[40px] text-cream tracking-tighter uppercase">REPORTER</h2>
          <p className="font-body-md text-[14px] text-sand leading-relaxed">
            Premium editorial publishing for Africa's travel storytellers. From Kruger to Cape Point — your journey deserves a magazine.
          </p>
        </div>
        <ul className="flex flex-wrap justify-center gap-6">
          {CATEGORIES.map((cat) => (
            <li key={cat}>
              <button
                onClick={() => {
                  setActiveCategory(cat);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="font-label-caps text-label-caps text-fog hover:text-terracotta transition-colors uppercase"
              >
                {cat}
              </button>
            </li>
          ))}
        </ul>
        <div className="w-full max-w-lg border-t border-ash/50 pt-8 flex justify-between items-center">
          <p className="font-meta-code text-[10px] uppercase tracking-widest text-fog">
            REPORTER © 2026 · MAROME INVESTMENTS
          </p>
          <p className="font-meta-code text-[10px] uppercase tracking-widest text-fog">
            Africa-first editorial
          </p>
        </div>
      </footer>
    </div>
  );
}
